import canvasSize from 'canvas-size'
import Cache from '@fs/zion-cache'
import { i18n } from '@fs/zion-locale'

import { PAGE_SIZES, S3_FETCH_OPTIONS } from './constants'

export const cache = new Cache({
  encrypted: false,
  lifetime: 'twoWeeks',
  storeName: 'zh_canvas_size',
  storeVersion: 1,
  type: 'local',
})

const DOES_SUPPORT_HD_CANVAS_KEY = 'zh_does_support_hd_canvas'
const MAX_CANVAS_AREA_KEY = 'zh_max_canvas_area'

export async function supportsHDCanvas() {
  const storedDoesSupportHDCanvasData = await cache.getItem(DOES_SUPPORT_HD_CANVAS_KEY)

  if (storedDoesSupportHDCanvasData !== undefined && storedDoesSupportHDCanvasData !== null) {
    return storedDoesSupportHDCanvasData
  }

  const doesSupportHDCanvas = await canvasSize
    .test({
      height: 10836,
      usePromise: true,
      width: 10836,
    })
    .then(() => true)
    .catch(() => false)

  cache.setItem(DOES_SUPPORT_HD_CANVAS_KEY, doesSupportHDCanvas)

  return doesSupportHDCanvas
}

export async function getMaxCanvasArea() {
  const storedMaxCanvasAreaData = await cache.getItem(MAX_CANVAS_AREA_KEY)

  if (storedMaxCanvasAreaData !== undefined && storedMaxCanvasAreaData !== null) {
    return storedMaxCanvasAreaData
  }

  const maxCanvasArea = await canvasSize
    .test({
      sizes: [
        [16384, 16384],
        [10836, 10836],
        [4096, 4096],
      ],
      usePromise: true,
    })
    .catch(() => ({ width: 4096, height: 4096 }))

  cache.setItem(MAX_CANVAS_AREA_KEY, maxCanvasArea)

  return maxCanvasArea
}

export function filenameFriendlyDate() {
  return new Date()
    .toISOString()
    .replace(/[:-]/g, '') // Replace colons and dashes
    .replace(/\.\d+(?=Z$)/, '') // Remove dot and milliseconds using a lookahead to keep the Z
}

export const TRANSPARENT_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
export function convertImageToDataUrl(src) {
  if (!src || src.startsWith('data:')) {
    return Promise.resolve(src)
  }

  // eslint-disable-next-line no-async-promise-executor -- Not going to refactor printing relating stuff
  return new Promise(async (resolve) => {
    try {
      const image = await fetch(src, S3_FETCH_OPTIONS)
      const blob = await image.blob()

      const reader = new FileReader()
      reader.addEventListener('load', () => resolve(reader.result), { once: true, capture: true })
      reader.readAsDataURL(blob)
    } catch (error) {
      resolve(TRANSPARENT_IMAGE)
    }
  })
}

// Find all images and replace them with a data URL (base64)
// if they are not already a data URL.
// This is needed for images to be included in the output.
export async function convertImagesToDataUrls(elementRef) {
  const imagesToProcess = Array.from(elementRef.current.querySelectorAll('img') || []).filter(
    (image) => image.src && !image.src.startsWith('data:')
  )
  const imageUrls = Array.from(new Set(imagesToProcess.map((image) => image.src)))
  const results = {}
  const processPromises = imageUrls.map((from) => {
    // eslint-disable-next-line no-async-promise-executor -- Not going to refactor printing relating stuff
    return new Promise(async (resolve) => {
      const to = await convertImageToDataUrl(from)
      results[from] = { to, listenForLoad: true }
      // eslint-disable-next-line no-promise-executor-return -- Not going to refactor printing relating stuff
      return resolve()
    })
  })
  await Promise.all(processPromises)
  const imagePromises = Array.from(imagesToProcess.length ? imagesToProcess : []).map((image) => {
    // eslint-disable-next-line no-async-promise-executor -- Not going to refactor printing relating stuff
    return new Promise(async (resolve) => {
      if (results[image.src].listenForLoad) {
        results[image.src].listenForLoad = false
        image.addEventListener('load', () => resolve(), { once: true, capture: true })
        setTimeout(() => resolve(), 3000) // If the image doesn't load, resolve anyway after 3 seconds
      } else {
        resolve()
      }
      image.src = results[image.src].to
    })
  })

  return Promise.all(imagePromises)
}

export function mmToPx(mm) {
  const millimetersPerInch = 25.4
  const pixelsPerInch = 96 // dpi or ppi
  const pixelsPerMillimeter = pixelsPerInch / millimetersPerInch

  return mm * pixelsPerMillimeter
}

export function pxToMm(px) {
  const millimetersPerInch = 25.4
  const pixelsPerInch = 96 // dpi or ppi
  const millimetersPerPixel = millimetersPerInch / pixelsPerInch

  return px * millimetersPerPixel
}

export function getPrintableArea({ pageSize, pageOrientation }) {
  let longEdge
  let shortEdge
  let printMargin

  if (Array.isArray(pageSize)) {
    longEdge = pxToMm(Math.max(...pageSize))
    shortEdge = pxToMm(Math.min(...pageSize))
    printMargin = 0
  } else {
    const size = PAGE_SIZES[pageSize]
    longEdge = size.longEdge
    shortEdge = size.shortEdge
    printMargin = size.printMargin
  }

  const pageHeight = pageOrientation === 'portrait' ? longEdge - printMargin * 3 : shortEdge - printMargin * 2
  const pageWidth = pageOrientation === 'portrait' ? shortEdge - printMargin * 2 : longEdge - printMargin * 3

  return {
    pageHeight,
    pageHeightPx: Math.floor(mmToPx(pageHeight)),
    pageWidth,
    pageWidthPx: Math.floor(mmToPx(pageWidth)),
    printMargin,
    printMarginPx: Math.floor(mmToPx(printMargin)),
  }
}

// Retrieves the correct scale to fit the contents on a page regardless of orientation
export function getScaleToRatio({ elementHeight, elementWidth, pageOrientation, pageSize }) {
  let pageWidthPx
  let pageHeightPx
  if (Array.isArray(pageSize)) {
    ;[pageWidthPx, pageHeightPx] = pageSize
  } else {
    const printableArea = getPrintableArea({ pageOrientation, pageSize })
    pageHeightPx = printableArea.pageHeightPx
    pageWidthPx = printableArea.pageWidthPx
  }

  return Math.floor(Math.min(pageHeightPx / elementHeight, pageWidthPx / elementWidth) * 100) / 100
}

function elementIndex(sibling, name) {
  if (sibling) {
    const add = sibling.localName === name ? 1 : 0

    return elementIndex(sibling.previousElementSibling, name || sibling.localName) + add
  }

  return 1
}

function xPathSegments(element, doc) {
  if (!element || element.nodeType !== 1) {
    return ['']
  }

  if (element.id && doc.getElementById(element.id) === element) {
    return [`id("${element.id}")`]
  }

  // In order to handle SVG or HTML elements
  const localName =
    element instanceof HTMLElement
      ? `${element.localName}[${elementIndex(element)}]`
      : `*[local-name()="${element.localName}"][${elementIndex(element)}]`

  return [...xPathSegments(element.parentNode, doc), localName]
}

export function getXPathForElement(element, doc = document) {
  return xPathSegments(element, doc).join('/')
}

export function getElementByXPath(path, doc = document, contextNode = null) {
  if (!path) {
    return null
  }

  const evaluator = new XPathEvaluator()
  const result = evaluator.evaluate(
    path,
    contextNode ?? doc.documentElement,
    null,
    XPathResult.FIRST_ORDERED_NODE_TYPE,
    null
  )
  return result.singleNodeValue
}

export function getElementsByXPath(path, doc = document) {
  if (!path) {
    return []
  }

  const results = []
  const evaluator = new XPathEvaluator()
  const query = evaluator.evaluate(path, doc.documentElement, null, XPathResult.ORDERED_NODE_SNAPSHOT_TYPE, null)
  for (let i = 0, length = query.snapshotLength; i < length; ++i) {
    results.push(query.snapshotItem(i))
  }
  return results
}

export function getUsabillaUrl(siteName = 'chinese') {
  // Global campaigns configuration uses one button/URL with language-specific forms, and sends to #campaignfeedback
  if (siteName === 'global') {
    return '//w.usabilla.com/02a612559cb1.js'
  }

  // Default configuration sends to #chinese-feedback
  const urlsByLocale = {
    en: '//w.usabilla.com/6698b8b4a23f.js',
    zh: '//w.usabilla.com/151ba38c0269.js',
    // `zh-hans`: '', // Not setup yet
  }

  return urlsByLocale[i18n.language] || urlsByLocale.zh
}

export const hasJapaneseName = (person) => {
  return person?.primaryNameForm?.lang?.includes('ja') || false
}

export const hasCJKName = (person) => {
  const lang = person?.primaryNameForm?.lang
  return lang?.includes('zh') || lang?.includes('ja') || lang?.includes('ko') || false
}

export async function dataURLtoFile(dataUrl, filename) {
  const response = await fetch(dataUrl)
  const blob = await response.blob()
  return new File([blob], filename, { type: blob.type })
}
